import { GridMenuItem } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { useTableApiContext } from '@/global/components/DataGrid/utils/table-context'
import { useTransmit } from '@/hooks/useTransmit'
import CircularProgress from '@/global/components/CircularProgress'
import { AxiosError } from 'axios'
import { FileArchiveIcon, FileUserIcon } from 'lucide-react'
import { espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { useCalculoStore } from './store'
import { EventEspelhoPdfMessage } from './types'

export default function BaixarCalculoButton({
    competencia,
    formato,
    selectAll,
    excludeIds,
    totalRows,
}: {
    competencia: string
    formato: 'pdf' | 'zip'
    selectAll: boolean
    excludeIds: number[]
    totalRows: number
}) {
    const empresaId = useStore(store => store.companyId)
    const costCenterId = useStore(store => store.costCenterId)
    const locaisIds = useStore(store => store.locaisIds)
    const state = useCalculoStore(store => store.state.state)
    const dispatch = useCalculoStore(store => store.dispatch)

    const { selectionModel } = useTableApiContext()

    const subscribe = useTransmit<EventEspelhoPdfMessage>({
        onMessage: async ({ status, data }, unsubscribe) => {
            switch (status) {
                case 'PROCESSANDO':
                    if (useCalculoStore.getState().state.progresso === null) {
                        toast.info(
                            'Estamos baixando os espelhos de ponto. Você será notificado quando estiver pronto. Fique à vontade para utilizar o sistema normalmente.',
                            {
                                duration: 5 * 1000,
                            },
                        )
                    }

                    dispatch({ action: 'PROGRESSO', data: data.progresso })

                    break
                case 'ERRO':
                    toast.error(data.message)
                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    break
                case 'FINALIZADO':
                    if (data.pdf) {
                        await espelhoPonto.calculos.file(`zip/${data.pdf}`)
                    }
                    if (data.zip) {
                        await espelhoPonto.calculos.file(`zip/${data.zip}`)
                    }

                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    break
            }
        },
    })

    const handleClick = async () => {
        if (!selectAll && selectionModel.length === 0) {
            toast.warning('Selecione pelo menos um espelho')
            return
        }

        dispatch({ action: 'PROCESSANDO', data: true })

        const count = selectAll
            ? totalRows - excludeIds.length
            : selectionModel.length

        if (formato === 'zip') {
            toast.info(
                `O arquivo está sendo gerado com ${count} espelhos, isso pode demorar um pouco...`,
            )
        }

        try {
            if (selectAll) {
                const payload = {
                    selectAll: true,
                    excludeIds: excludeIds.length > 0 ? excludeIds : undefined,
                    competencia,
                    centroCustoId: costCenterId > 0 ? costCenterId : undefined,
                    locaisIds: locaisIds.length > 0 ? locaisIds : undefined,
                }

                if (formato === 'pdf') {
                    const channel = await espelhoPonto.calculos.post<
                        typeof payload,
                        string
                    >(`pdf?empresaId=${empresaId}`, payload)
                    await subscribe(channel)
                } else {
                    const channel: string = await espelhoPonto.calculos.zip({
                        data: payload as any,
                        isBlob: false,
                        queryParam: { competencia, empresaId },
                    })
                    await subscribe(channel)
                }
            } else {
                const ids = selectionModel as number[]

                if (formato === 'pdf') {
                    const channel = await espelhoPonto.calculos.post<
                        number[],
                        string
                    >(`pdf?empresaId=${empresaId}`, ids)
                    await subscribe(channel)
                } else {
                    const channel: string = await espelhoPonto.calculos.zip({
                        data: ids,
                        isBlob: false,
                        queryParam: { competencia, empresaId },
                    })
                    await subscribe(channel)
                }
            }
        } catch (error) {
            dispatch({ action: 'PROCESSANDO', data: false })
            const axiosError = error as AxiosError

            if (axiosError.message) {
                toast.error(axiosError.message)
            } else {
                toast.error('Erro ao gerar arquivo')
            }
        }
    }

    const getLabel = () => {
        const type = formato === 'zip' ? 'ZIP' : 'PDF'

        if (selectAll) {
            return excludeIds.length > 0
                ? `Exportar todos (-${excludeIds.length}) como ${type}`
                : `Exportar todos como ${type}`
        }

        return `Exportar selecionados como ${type}`
    }

    const isDisabled =
        (!selectAll && selectionModel.length === 0) || state !== 'idle'

    return (
        <GridMenuItem
            startIcon={(() => {
                if (state !== 'idle') return <CircularProgress size={24} />
                if (formato === 'zip')
                    return <FileArchiveIcon className="mr-2" />
                return <FileUserIcon className="mr-2" />
            })()}
            label={getLabel()}
            onClick={handleClick}
            disabled={isDisabled}
        />
    )
}
